import type {
  ConnectionRef,
  PublicSystemAgentItem,
  SystemAgentCategoryItem,
} from "@thyra/client";
import type { CATALOG_TOOL_NAMES } from "@thyra/ui/tool-icon";

/**
 * Pure helpers behind the landing "ready-to-use agents" section: normalising the public catalog payload,
 * the cadence of an agent, category chips and filtering, keyboard navigation of the chips, and provider
 * logo resolution. No React, no DOM, so they are unit-testable in isolation.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Only a real template id may be deep-linked into the app; anything else would land on an invalid template. */
export function isTemplateId(value: string | null | undefined): boolean {
  return Boolean(value && UUID_RE.test(value));
}

export type Cadence =
  | { kind: "interval"; unit: "minutes" | "hours"; count: number }
  | { kind: "daily"; time: string }
  | { kind: "weekdays"; time: string }
  | { kind: "weekly"; days: number[]; time: string }
  | { kind: "scheduled" };

export type CatalogConnection = {
  key: string;
  name: string;
};

export type CatalogAgent = {
  id: string;
  name: string;
  description: string | null;
  iconKey: string | null;
  category: string | null;
  highlights: string[];
  requiresApproval: boolean;
  cadence: Cadence | null;
  connections: CatalogConnection[];
  /** Accounts it works better with but never needs; never counted as required. */
  optionalConnections: CatalogConnection[];
  /** Capability tools only (browser, search, ...), most important first; prompt-level tools are left out. */
  tools: string[];
  /** The template's own safety rules and authored instructions; 0 when the API does not say (then not shown). */
  guardrails: number;
  instructions: number;
  /** Days memories are kept; null when it remembers without a stated limit. Absent when it has no memory. */
  memory: { retentionDays: number | null } | null;
  knowledgeBase: boolean;
};

function positiveInt(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

/** Tools that give the user a capability, most important first. Anything else is prompt-level. */
export type CatalogToolName = (typeof CATALOG_TOOL_NAMES)[number];

export const CAPABILITY_TOOL_ORDER: readonly CatalogToolName[] = [
  "Browser Automation",
  "Web Search",
  "Python Interpreter",
  "RAG",
  "Long Memory",
  "Notify",
  "Request Approval",
  "Calculator",
  "Vision Extract",
];

export function capabilityToolsOf(
  tools: readonly string[] | null | undefined,
): string[] {
  const present = new Set(tools ?? []);
  return CAPABILITY_TOOL_ORDER.filter((t) => present.has(t));
}

export type CatalogCategory = {
  slug: string;
  label: string;
  iconKey: string | null;
  count: number;
};

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Cadence of an agent from its schedule fields; null when it does not run on its own. */
export function cadenceOf(
  agent: Pick<
    PublicSystemAgentItem,
    | "auto_run_enabled"
    | "schedule_type"
    | "schedule_hour"
    | "schedule_minute"
    | "schedule_weekdays"
    | "schedule_interval_minutes"
  >,
): Cadence | null {
  if (!agent.auto_run_enabled) return null;
  const hour = agent.schedule_hour;
  const hasTime =
    typeof hour === "number" &&
    Number.isInteger(hour) &&
    hour >= 0 &&
    hour <= 23;
  const minuteRaw = agent.schedule_minute ?? 0;
  const minute =
    Number.isInteger(minuteRaw) && minuteRaw >= 0 && minuteRaw <= 59
      ? minuteRaw
      : 0;
  const time = hasTime ? `${pad2(hour)}:${pad2(minute)}` : null;

  switch (agent.schedule_type) {
    case "interval": {
      const minutes = agent.schedule_interval_minutes;
      if (!minutes || minutes < 1 || !Number.isFinite(minutes)) {
        return { kind: "scheduled" };
      }
      return minutes >= 60 && minutes % 60 === 0
        ? { kind: "interval", unit: "hours", count: minutes / 60 }
        : { kind: "interval", unit: "minutes", count: Math.round(minutes) };
    }
    case "daily":
      return time ? { kind: "daily", time } : { kind: "scheduled" };
    case "weekly": {
      const days = [
        ...new Set((agent.schedule_weekdays ?? []).filter(isWeekday)),
      ].sort((a, b) => a - b);
      if (!time || days.length === 0) return { kind: "scheduled" };
      if (days.length === 7) return { kind: "daily", time };
      if (days.join() === "0,1,2,3,4") return { kind: "weekdays", time };
      return { kind: "weekly", days, time };
    }
    default:
      return { kind: "scheduled" };
  }
}

function isWeekday(n: unknown): n is number {
  return typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= 6;
}

/** Localised, short weekday names for days where 0 = Monday ("Mon, Wed" / "seg., qua."). */
export function formatWeekdays(days: number[], locale: string): string {
  // 2024-01-01 was a Monday.
  const names = days.map((d) =>
    new Intl.DateTimeFormat(locale, {
      weekday: "short",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(2024, 0, 1 + d))),
  );
  try {
    return new Intl.ListFormat(locale, {
      style: "short",
      type: "conjunction",
    }).format(names);
  } catch {
    return names.join(", ");
  }
}

/** Humanised provider key, used only when the API supplied no name. */
export function humanizeProviderKey(key: string): string {
  return key
    .split(/[-_]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function toConnection(
  key: string,
  ref: Pick<ConnectionRef, "name"> | undefined,
): CatalogConnection {
  return {
    key,
    name: ref?.name?.trim() || humanizeProviderKey(key),
  };
}

export function normalizeAgent(
  raw: PublicSystemAgentItem,
): CatalogAgent | null {
  if (!raw || !raw.agent_id || !raw.name) return null;
  const refs = new Map(
    (raw.required_connections ?? []).map((c) => [c.provider_key, c]),
  );
  const keys = raw.required_connection_provider_keys?.length
    ? raw.required_connection_provider_keys
    : (raw.required_connections ?? []).map((c) => c.provider_key);
  const seen = new Set<string>();
  const connections: CatalogConnection[] = [];
  for (const key of keys) {
    if (!key || seen.has(key)) continue;
    seen.add(key);
    connections.push(toConnection(key, refs.get(key)));
  }
  const optionalConnections: CatalogConnection[] = [];
  for (const ref of raw.optional_connections ?? []) {
    const key = ref?.provider_key;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    optionalConnections.push(toConnection(key, ref));
  }
  return {
    id: raw.agent_id,
    name: raw.name,
    description: raw.description?.trim() || null,
    iconKey: raw.icon_key ?? null,
    category: raw.category ?? null,
    highlights: (raw.highlights ?? []).map((h) => h.trim()).filter(Boolean),
    requiresApproval:
      raw.requires_approval ??
      (raw.tools ?? []).some((tool) => /request approval/i.test(tool)),
    cadence: cadenceOf(raw),
    connections,
    optionalConnections,
    tools: capabilityToolsOf(raw.tools),
    guardrails: positiveInt(raw.guardrail_count),
    instructions: positiveInt(raw.instruction_count),
    memory: raw.memory?.enabled
      ? { retentionDays: positiveInt(raw.memory.retention_days) || null }
      : null,
    knowledgeBase: Boolean(raw.knowledge_base?.enabled),
  };
}

export function normalizeAgents(raw: unknown): CatalogAgent[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => normalizeAgent(item as PublicSystemAgentItem))
    .filter((a): a is CatalogAgent => a !== null);
}

/**
 * Chips for the categories that really have agents, in the API's order. Counts are recomputed from the
 * loaded agents so a chip never promises more (or fewer) cards than the grid shows.
 */
export function buildCategories(
  categories: readonly SystemAgentCategoryItem[],
  agents: readonly CatalogAgent[],
): CatalogCategory[] {
  const counts = new Map<string, number>();
  for (const agent of agents) {
    if (agent.category) {
      counts.set(agent.category, (counts.get(agent.category) ?? 0) + 1);
    }
  }
  return categories
    .filter((c) => (counts.get(c.slug) ?? 0) > 0)
    .map((c) => ({
      slug: c.slug,
      label: c.label,
      iconKey: c.icon_key ?? null,
      count: counts.get(c.slug) ?? 0,
    }));
}

/** null selects every agent. */
export function filterByCategory(
  agents: readonly CatalogAgent[],
  slug: string | null,
): CatalogAgent[] {
  return slug ? agents.filter((a) => a.category === slug) : [...agents];
}

/** Roving-tabindex arrow handling for the chips; null when the key is not a navigation key. */
export function nextChipIndex(
  current: number,
  key: string,
  length: number,
): number | null {
  if (length <= 0) return null;
  switch (key) {
    case "ArrowRight":
    case "ArrowDown":
      return (current + 1) % length;
    case "ArrowLeft":
    case "ArrowUp":
      return (current - 1 + length) % length;
    case "Home":
      return 0;
    case "End":
      return length - 1;
    default:
      return null;
  }
}

/**
 * Slugs of the logos bundled under public/connections, listed from the directory by vite.config.ts at
 * build time (empty when not injected, e.g. under unit tests, which pass their own set).
 */
declare const __CONNECTION_LOGO_SLUGS__: readonly string[] | undefined;
const BUNDLED_LOGO_SLUGS = new Set<string>(
  typeof __CONNECTION_LOGO_SLUGS__ === "undefined"
    ? []
    : __CONNECTION_LOGO_SLUGS__,
);

/** Logo for a connection: its provider key when that file is bundled, else null (initial tile). */
export function connectionLogo(
  providerKey: string,
  available: ReadonlySet<string> = BUNDLED_LOGO_SLUGS,
): string | null {
  return available.has(providerKey) ? `/connections/${providerKey}.svg` : null;
}

/** Letter for the neutral tile shown when a provider has no bundled logo. */
export function connectionInitial(name: string): string {
  return (Array.from(name.trim())[0] ?? "?").toUpperCase();
}
